import { z } from 'zod';
import { CalendarDay } from 'further-ui/utils';
import {
  blockNoteEditor,
  periodEndReport as rules,
} from 'further-ui/validations';

const recipientSchema = z.object({
  investorId: z.string(),
  investorName: z.string(),
  totalCommitment: z.number(),
  paidInCapital: z.number(),
  notifyViaEmail: z.boolean(),
});

const notificationPreferencesSchema = z.object({
  notifyViaEmail: z.boolean(),
  attachPdfUpdateToEmail: z.boolean(),
});

const portfolioUpdateFileSchema = z
  .object({
    name: z.string(),
    path: z.string(),
    previewUrl: z.string(),
  })
  .optional();

const draftSchema = z.object({
  useFullValidation: z.literal(false),
  id: z.string().optional(),
  title: z.string().optional(),
  reportDate: z.instanceof(CalendarDay).optional(),
  commentary: blockNoteEditor.blockNoteDocumentSchema.optional(),
  fundOverviewDatapoints: rules.fundOverviewDatapointsSchema,
  portfolioUpdateFile: portfolioUpdateFileSchema,
  notificationPreferences: notificationPreferencesSchema,
  recipients: z.array(recipientSchema),
  resendPublishedUpdates: z.boolean(),
});

const fullValidationSchema = z.object({
  useFullValidation: z.literal(true),
  id: z.string().optional(),
  title: z.string().min(1, 'Report name is required.'),
  reportDate: z.instanceof(CalendarDay, {
    message: 'Report publication date is required.',
  }),
  commentary: blockNoteEditor.blockNoteDocumentSchema.optional(),
  fundOverviewDatapoints: rules.fundOverviewDatapointsSchema.superRefine(
    (value, ctx) => {
      const requireValueIfIncluded = (args: {
        included: boolean;
        fieldValue: string;
        label: string;
        path: Array<string>;
      }) => {
        if (!args.included) return;
        if (args.fieldValue.trim().length > 0) return;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${args.label} is required.`,
          path: args.path,
        });
      };

      requireValueIfIncluded({
        included: value.general.fundTerm.included,
        fieldValue: value.general.fundTerm.value,
        label: 'Fund term',
        path: ['general', 'fundTerm', 'value'],
      });
      requireValueIfIncluded({
        included: value.general.legalForm.included,
        fieldValue: value.general.legalForm.value,
        label: 'Legal form',
        path: ['general', 'legalForm', 'value'],
      });
      requireValueIfIncluded({
        included: value.general.investmentAdviserManager.included,
        fieldValue: value.general.investmentAdviserManager.value,
        label: 'Investment adviser/manager',
        path: ['general', 'investmentAdviserManager', 'value'],
      });
      requireValueIfIncluded({
        included: value.general.generalPartner.included,
        fieldValue: value.general.generalPartner.value,
        label: 'General partner',
        path: ['general', 'generalPartner', 'value'],
      });
      requireValueIfIncluded({
        included: value.general.reinvestmentRecyclingPolicy.included,
        fieldValue: value.general.reinvestmentRecyclingPolicy.value,
        label: 'Reinvestment/recycling policy',
        path: ['general', 'reinvestmentRecyclingPolicy', 'value'],
      });

      requireValueIfIncluded({
        included: value.investmentFocus.stage.included,
        fieldValue: value.investmentFocus.stage.value,
        label: 'Stage',
        path: ['investmentFocus', 'stage', 'value'],
      });
      requireValueIfIncluded({
        included: value.investmentFocus.sector.included,
        fieldValue: value.investmentFocus.sector.value,
        label: 'Sector',
        path: ['investmentFocus', 'sector', 'value'],
      });
      requireValueIfIncluded({
        included: value.investmentFocus.geography.included,
        fieldValue: value.investmentFocus.geography.value,
        label: 'Geography',
        path: ['investmentFocus', 'geography', 'value'],
      });
    },
  ),
  portfolioUpdateFile: portfolioUpdateFileSchema,
  notificationPreferences: notificationPreferencesSchema,
  recipients: z.array(recipientSchema),
  resendPublishedUpdates: z.boolean(),
});

export const schema = z.discriminatedUnion('useFullValidation', [
  draftSchema,
  fullValidationSchema,
]);

export type PeriodEndReportFormValues = z.infer<typeof schema>;

export type FullyValidatedPeriodEndReportFormValues = z.infer<
  typeof fullValidationSchema
>;
